<template>
    <div class="relative z-120">
        <CustomMenu
            v-model="menu"
            hideTransitionSlider
        >
            <template #activator>
                <div
                    class="font-sans-rubik flex justify-between items-center cursor-pointer relative"
                    :class="mini && 'justify-center'"
                    @click="menuHandler"
                >
                    <div class="group flex p-2 items-center min-mob:hover:bg-menu-purple70 min-mob:dark:hover:bg-menu-blue770 rounded-1.25 transition-colors">
                        <img
                            v-if="currentWorkspace.image"
                            class="min-w-6 min-h-6 max-w-6 max-h-6 rounded-0.75"
                            :class="{ 'mr-3': !mini }"
                            :src="currentWorkspace.image"
                        >

                        <DefaultAvatarWorkspace
                            v-else
                            :class="{ 'mr-2': !mini }"
                            :name="currentWorkspace.name"
                            bgClass="bg-primary-gradient"
                            textClass="text-white"
                        />

                        <div
                            v-if="!mini"
                            class="max-w-24 truncate text-gray-370 dark:text-white font-medium text-xs select-none"
                        >
                            {{ !!currentWorkspace ? currentWorkspace.name : '' }}
                        </div>

                        <ArrowVerticallyIcon
                            v-if="!mini"
                            class="w-4 h-4 text-gray-370 dark:text-gray-600 group-hover:text-gray-370 dark:group-hover:text-white transition-colors"
                        />
                    </div>

                    <div
                        v-if="!mini && !isMbView"
                        class="group min-w-6 min-h-6 max-h-6 relative left-0.75 flex items-center justify-center rounded-1.25 hover:bg-menu-purple70 dark:hover:bg-menu-blue770 cursor-pointer"
                        @click.stop="onChange"
                    >
                        <DoubleArrowIcon class="text-gray-600 dark:text-gray-600 dark:group-hover:text-gray-200 rotate-180" />
                    </div>
                </div>
            </template>

            <template #dropdown>
                <div class="font-sans-rubik w-61 overflow-hidden absolute -top-12.5 -left-0.5 bg-purple-60 dark:bg-blue-810 border border-gray-100 dark:border-gray-760 rounded-1.25 shadow-activeMenu dark:shadow-activeMenuDark">
                    <div class="pt-3 pb-3 px-4 border-b-1 border-gray-100 dark:border-gray-760">
                        <div class="flex items-center gap-x-2">
                            <img
                                v-if="currentWorkspace.image"
                                class="min-w-6 min-h-6 max-w-6 max-h-6 rounded-1.25"
                                :src="currentWorkspace.image"
                            >

                            <DefaultAvatarWorkspace
                                v-else
                                :name="currentWorkspace.name"
                                mini
                                bgClass="bg-primary-gradient"
                                textClass="text-white"
                            />

                            <div class="min-w-31.25 truncate text-purple-200 dark:text-white font-medium text-xs select-none">
                                {{ !!currentWorkspace ? currentWorkspace.name : '' }}
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="workspaces.length"
                        class="max-h-46.5 py-1 custom-scroll-y"
                    >
                        <template
                            v-for="(workspace, index) in workspaces"
                            :key="workspace.slug"
                        >
                            <div
                                class="flex items-center gap-x-2.5 h-9 mx-2 px-2 mb-0.5 rounded-1.25 text-gray-370 dark:text-white hover:text-gray-370 dark:hover:text-white hover:bg-purple-70 dark:hover:bg-blue-780 transition-colors"
                                :class="loading ? 'cursor-wait' : 'cursor-pointer'"
                                @click.stop="changeWorkspace(workspace)"
                                @mouseenter="onMouseEnter(index)"
                                @mouseleave="onMouseLeave"
                            >
                                <img
                                    v-if="workspace.image"
                                    class="w-5 h-5 rounded-1.25"
                                    :src="workspace.image"
                                >

                                <DefaultAvatarWorkspace
                                    v-else
                                    :name="workspace.name"
                                    mini
                                    bgClass="bg-purple-50 dark:bg-blue-770"
                                />

                                <div class="max-w-32.5 truncate text-xs">
                                    {{ workspace.name }}
                                </div>
                            </div>
                        </template>
                    </div>

                    <div
                        v-if="isOwner && !authorizationStore.isAccountReview"
                        class="border-t border-gray-100 dark:border-gray-760"
                    ></div>

                    <div
                        v-if="isOwner && !authorizationStore.isAccountReview"
                        class="my-1.5 flex items-center gap-x-2 h-9 mx-2 px-2 rounded-1.25 text-purple dark:text-purple-140 hover:bg-purple-70 dark:hover:bg-blue-780 cursor-pointer transition-colors"
                        @click="openCreateDialog"
                    >
                        <CreateWorkspaceIcon class="w-5 h-5" />

                        <div class="text-xs">
                            Create Workspace
                        </div>
                    </div>
                </div>
            </template>
        </CustomMenu>

        <CreateWorkspaceDialog v-model="showCreateDialog" />
    </div>
</template>

<script setup>
import {
    ref, computed, watch, inject,
} from 'vue'
import { useRouter } from 'vue-router'

import CustomMenu from '@/components/menu/CustomMenu/CustomMenu.vue'
import ArrowVerticallyIcon from '@/components/icons/arrow/ArrowVerticallyIcon.vue'
import CreateWorkspaceDialog from '@/components/dialogs/CreateWorkspaceDialog.vue'
import DefaultAvatarWorkspace from '@/components/defaultAvatars/DefaultAvatarWorkspace.vue'
import DoubleArrowIcon from '@/components/icons/arrow/DoubleArrowIcon.vue'
import CreateWorkspaceIcon from '@/components/icons/CreateWorkspaceIcon.vue'

import { useAuthorizationStore } from '@/store/authorizationStore'
import { useSortFilterTableStore } from '@/store/sortFilterTableStore'
import { useTwoFactorAuthentication } from '@/store/call-tracking/twoFactorAuthentication'

import { ROUTES } from '@/constants/routes'
import { viewPorts } from '@/constants/viewPorts'

const props = defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
    isOwner: {
        type: Boolean,
        default: false,
    },
})

const emits = defineEmits(['onChange'])

const authorizationStore = useAuthorizationStore()
const sortFilterTableStore = useSortFilterTableStore()
const twoFactorAuthentication = useTwoFactorAuthentication()

const router = useRouter()

const currentViewSize = inject('currentViewSize')

const loading = ref(false)

const menu = ref(false)
const showCreateDialog = ref(false)
const activeIndex = ref(-1)

const isMbView = computed(() => viewPorts.mb >= currentViewSize.value)
const currentWorkspace = computed(() => authorizationStore.workspace || {})
const workspaces = computed(() => authorizationStore.workspaces.filter((w) => w.slug !== currentWorkspace.value.slug))
const workspaceSlug = computed(() => (currentWorkspace.value ? currentWorkspace.value.slug : null))

function openCreateDialog() {
    showCreateDialog.value = true
    closeMenu()
}

function onMouseEnter(index) {
    activeIndex.value = index
}

function onMouseLeave() {
    activeIndex.value = -1
}

function menuHandler() {
    if (props.isOwner && authorizationStore.isRequiredUserTwoFactor && import.meta.env.VITE_NODE_ENV !== 'local') {
        twoFactorAuthentication.setConfirmDialogVisible(true)
        return
    }

    menu.value = !menu.value
}

function closeMenu() {
    menu.value = false
}

function onChange() {
    emits('onChange', !props.mini)
}

async function changeWorkspace(workspace) {
    if (workspaceSlug.value === workspace.slug) {
        return
    }

    if (loading.value) {
        return
    }

    loading.value = true

    await authorizationStore.getCheckWorkspace(workspace.slug)

    closeMenu()

    loading.value = false
}

watch(workspaceSlug, (current, old) => {
    if (!!current && !!old) {
        sortFilterTableStore.removeFilters()
        sortFilterTableStore.clearTableSettings()

        const { owner_blocked } = currentWorkspace.value

        if (owner_blocked) {
            router.push({ name: ROUTES.accountDisabled })
        } else {
            router.push({ name: ROUTES.reporting, params: { workspace: current } })
        }
    } else {
        router.push({ name: ROUTES.authLogin })
    }
})
</script>
